/**
 * The Steward Terms of Service acceptance record (steward-app#2630).
 *
 * The Terms say "By creating an account or using the Service, you agree to these Terms".
 * Before this record, nothing asked a person to agree and nothing showed that they had,
 * which leaves the Terms as browsewrap. This is the clickwrap half: every way into a
 * workspace shows the acceptance line beside the button, the click records a versioned
 * acceptance, and the façade refuses to create or join a workspace without one.
 *
 * Same shape as the credential vault agreement (`credentialVault.ts`), read on both sides
 * of the API for the same reason.
 */

/**
 * Current substance version of the Terms of Service and Privacy Policy a person accepts.
 *
 * Bump ONLY on a material change to either document (the Terms promise 30 days' notice of
 * one). Everyone holding an older version is asked again at their next sign-in. A wording
 * tweak does not bump it. Before bumping, build the acceptance history (steward-app#2633);
 * `termsAcceptance.test.ts` fails on any bump until then. The documents each version stood
 * for are steward-site's `src/pages/terms.astro` and `privacy.astro` as published when the
 * number was set.
 */
export const TERMS_VERSION = 1;

/**
 * The two preference keys that record an acceptance. They are written ONLY by the façade's
 * `PUT /me`, from its `termsAcceptance` field, with a time the façade stamps itself; it
 * refuses a `preferences` body that names either key. `me.test.ts` pins both halves.
 */
export const TERMS_ACCEPTED_AT_KEY = 'termsAcceptedAt';
export const TERMS_VERSION_KEY = 'termsVersion';
export const TERMS_PREFERENCE_KEYS = [TERMS_ACCEPTED_AT_KEY, TERMS_VERSION_KEY] as const;

/** The `ForbiddenError` code the façade refuses a workspace create or join with. */
export const TERMS_ACCEPTANCE_REQUIRED = 'TERMS_ACCEPTANCE_REQUIRED';

/**
 * Has this person accepted the CURRENT version of the Terms and Privacy Policy?
 *
 * An absent version is NOT read as version 1: no acceptance was ever recorded without one.
 */
export function hasTermsAcceptance(preferences: Record<string, unknown> | null | undefined): boolean {
  if (!preferences) return false;
  const at = preferences[TERMS_ACCEPTED_AT_KEY];
  const version = preferences[TERMS_VERSION_KEY];
  return typeof at === 'string' && at.length > 0
    && typeof version === 'number' && version >= TERMS_VERSION;
}

/** Where the line links. The published steward-site pages, the same in every env. */
export const TERMS_OF_SERVICE_URL = 'https://steward.crossmarc.com/terms';
export const PRIVACY_POLICY_URL = 'https://steward.crossmarc.com/privacy';
